/**
 * UTILIDADES DE FECHA SEGURAS (VERSIÓN ROBUSTA)
 * Evita errores "Invalid time value" al procesar fechas
 * Incluye validación de formato, bloqueo de fechas futuras y manejo seguro
 */

/**
 * Verifica si un string tiene formato de fecha completo (YYYY-MM-DD o DD/MM/AAAA)
 * @param {string} valor - String a verificar
 * @returns {boolean} True si el formato es válido y completo
 */
export function isFechaCompleta(valor) {
  if (!valor || typeof valor !== 'string') return false
  
  // Formato YYYY-MM-DD (input type="date")
  const regexISO = /^\d{4}-\d{2}-\d{2}$/
  if (regexISO.test(valor)) {
    const partes = valor.split('-')
    const año = parseInt(partes[0], 10)
    const mes = parseInt(partes[1], 10)
    const dia = parseInt(partes[2], 10)
    if (mes >= 1 && mes <= 12 && dia >= 1 && dia <= 31) return true
  }
  
  // Formato DD/MM/AAAA
  const regexLocale = /^\d{2}\/\d{2}\/\d{4}$/
  if (regexLocale.test(valor)) {
    const partes = valor.split('/')
    const dia = parseInt(partes[0], 10)
    const mes = parseInt(partes[1], 10)
    const año = parseInt(partes[2], 10)
    if (mes >= 1 && mes <= 12 && dia >= 1 && dia <= 31) return true
  }
  
  return false
}

/**
 * Convierte un valor a Date solo si es válido
 * @param {any} valor - Valor a convertir (string, Date, null, undefined)
 * @param {Date} [defaultValue=null] - Valor por defecto si es inválido
 * @param {boolean} [bloquearFuturo=false] - Si true, retorna null para fechas futuras
 * @returns {Date|null} Fecha válida o null
 */
export function toDateSafe(valor, defaultValue = null, bloquearFuturo = false) {
  if (!valor) return defaultValue
  
  try {
    let d = null
    
    // Si ya es Date
    if (valor instanceof Date) {
      d = valor
    }
    // Si es string
    else if (typeof valor === 'string') {
      // Evitar strings vacíos
      if (valor.trim() === '') return defaultValue
      
      // Intentar crear Date directamente (funciona con ISO y otros formatos)
      d = new Date(valor)
      
      // Verificar que sea válida
      if (isNaN(d.getTime())) return defaultValue
      
      // Si es formato DD/MM/AAAA, validar día/mes
      const regexDDMMYYYY = /^\d{2}\/\d{2}\/\d{4}$/
      if (regexDDMMYYYY.test(valor)) {
        const partes = valor.split('/')
        const dia = parseInt(partes[0], 10)
        const mes = parseInt(partes[1], 10) - 1
        const año = parseInt(partes[2], 10)
        if (d.getDate() !== dia || d.getMonth() !== mes || d.getFullYear() !== año) {
          return defaultValue
        }
      }
    }
    
    if (!d || isNaN(d.getTime())) return defaultValue
    
    // Bloquear fechas futuras si se solicita
    if (bloquearFuturo && d > new Date()) return defaultValue
    
    return d
  } catch {
    return defaultValue
  }
}

/**
 * Formatea una fecha a ISO string (YYYY-MM-DD) de forma segura
 * @param {any} fecha - Fecha a formatear
 * @param {string} [defaultValue=''] - Valor por defecto
 * @returns {string} Fecha ISO o string vácio
 */
export function toISOStringSafe(fecha, defaultValue = '') {
  const d = toDateSafe(fecha)
  if (!d) return defaultValue
  try {
    return d.toISOString().split('T')[0]
  } catch {
    return defaultValue
  }
}

/**
 * Formatea una fecha a locale string de forma segura
 * @param {any} fecha - Fecha a formatear
 * @param {string} [locale='es-AR'] - Locale
 * @param {Object} [options={}] - Opciones de formateo
 * @param {string} [defaultValue='N/A'] - Valor por defecto
 * @returns {string} Fecha formateada o 'N/A'
 */
export function toLocaleDateStringSafe(fecha, locale = 'es-AR', options = {}, defaultValue = 'N/A') {
  const d = toDateSafe(fecha)
  if (!d) return defaultValue
  try {
    return d.toLocaleDateString(locale, options)
  } catch {
    return defaultValue
  }
}

/**
 * Calcula la diferencia en años entre dos fechas de forma segura
 * @param {any} desde - Fecha inicial
 * @param {any} hasta - Fecha final
 * @returns {number} Diferencia en años (decimal)
 */
export function diferenciaAnosSafe(desde, hasta) {
  const d1 = toDateSafe(desde)
  const d2 = toDateSafe(hasta, new Date())
  if (!d1 || !d2) return 0
  try {
    return (d2 - d1) / (1000 * 60 * 60 * 24 * 365.25)
  } catch {
    return 0
  }
}
